import { useCallback } from 'react';
import { Form, Formik } from 'formik';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import { TravelDocumentsListAddMutation } from '__generated__/TravelDocumentsListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
};

const TravelDocumentsListAdd = ({ connectionId }: Props) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<TravelDocumentsListAddMutation>(graphql`
    mutation TravelDocumentsListAddMutation(
      $input: CreateTravelDocumentInput!
      $connections: [ID!]!
    ) {
      createTravelDocument(input: $input) {
        node
          @prependNode(
            connections: $connections
            edgeTypeName: "TravelDocumentEdge"
          ) {
          ...TravelDocumentsListRow_document
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionId], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionId, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas kelionės documento tipas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują kelionės dokumento tipą.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus fullWidth name="name" label="Pavadinimas" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default TravelDocumentsListAdd;
